/**
 * FEATURED PRODUCT SPECTACULAR
 * JavaScript animations and interactions
 * Requires GSAP 3.x
 */

(function() {
  'use strict';

  // Check if GSAP is loaded
  if (typeof gsap === 'undefined') {
    console.warn('GSAP not loaded - spectacular animations disabled');
    return;
  }

  const spectacular = document.querySelector('.featured-spectacular');
  if (!spectacular) return;

  const config = {
    showPaparazzi: spectacular.dataset.showPaparazzi === 'true',
    showSpotlight: spectacular.dataset.showSpotlight === 'true',
    showConfetti: spectacular.dataset.showConfetti === 'true',
  };

  /**
   * PAPARAZZI FLASH EFFECT
   * Random camera flashes around the page
   */
  function initPaparazziFlashes() {
    if (!config.showPaparazzi) return;

    const container = spectacular.querySelector('.paparazzi-flashes');
    if (!container) return;

    function createFlash() {
      const flash = document.createElement('div');
      flash.className = 'flash';

      // Random position
      const x = Math.random() * 100;
      const y = Math.random() * 80; // Keep in upper 80% of screen

      flash.style.left = `${x}%`;
      flash.style.top = `${y}%`;

      container.appendChild(flash);

      // Remove after animation completes
      setTimeout(() => {
        flash.remove();
      }, 400);
    }

    // Create flashes at random intervals
    function scheduleNextFlash() {
      const delay = 1000 + Math.random() * 3000; // 1-4 seconds
      setTimeout(() => {
        createFlash();
        scheduleNextFlash();
      }, delay);
    }

    // Start the flash sequence
    scheduleNextFlash();

    // Initial burst of flashes
    setTimeout(() => createFlash(), 500);
    setTimeout(() => createFlash(), 800);
    setTimeout(() => createFlash(), 1200);
  }

  /**
   * CONFETTI PARTICLES
   * Falling confetti in brand colors
   */
  function initConfetti() {
    if (!config.showConfetti) return;

    const container = spectacular.querySelector('.confetti-container');
    if (!container) return;

    const colors = ['confetti-green', 'confetti-yellow', 'confetti-charcoal'];
    const confettiCount = 90;

    function createConfetti() {
      const confetti = document.createElement('div');
      confetti.className = `confetti ${colors[Math.floor(Math.random() * colors.length)]}`;

      // Random horizontal position
      const x = Math.random() * 100;
      confetti.style.left = `${x}%`;
      confetti.style.top = '-20px';

      // Random animation duration (fall speed)
      const duration = 3 + Math.random() * 4; // 3-7 seconds
      confetti.style.animationDuration = `${duration}s`;

      // Random delay
      const delay = Math.random() * 2;
      confetti.style.animationDelay = `${delay}s`;

      container.appendChild(confetti);

      // Remove after falling
      setTimeout(() => {
        confetti.remove();
      }, (duration + delay) * 1000);
    }

    // Create initial batch of confetti
    for (let i = 0; i < confettiCount; i++) {
      setTimeout(() => createConfetti(), i * 100);
    }

    // Continuously create new confetti (3x at a time for triple density)
    setInterval(() => {
      createConfetti();
      createConfetti();
      createConfetti();
    }, 500);
  }

  /**
   * PRODUCT IMAGE ENTRANCE
   * Dramatic zoom-in entrance with "WHOOSH"
   */
  function animateProductEntrance() {
    const productWrapper = spectacular.querySelector('.spectacular-product-wrapper');
    if (!productWrapper) return;

    gsap.fromTo(
      productWrapper,
      {
        scale: 0.5,
        opacity: 0,
        rotateZ: -15,
      },
      {
        scale: 1,
        opacity: 1,
        rotateZ: -2,
        duration: 1.2,
        ease: 'elastic.out(1, 0.6)',
        delay: 0.3,
      }
    );
  }

  /**
   * AWARD SEALS ENTRANCE
   * Seals bounce in one by one
   */
  function animateAwardSeals() {
    const seals = spectacular.querySelectorAll('.award-seal');
    if (!seals.length) return;

    seals.forEach((seal, index) => {
      gsap.fromTo(
        seal,
        {
          scale: 0,
          opacity: 0,
          rotateZ: 0,
        },
        {
          scale: 1,
          opacity: 1,
          rotateZ: seal.dataset.rotation || 15,
          duration: 0.6,
          ease: 'back.out(2)',
          delay: 0.8 + index * 0.15,
        }
      );
    });
  }

  /**
   * HEADLINE ENTRANCE
   * Text appears with bounce
   */
  function animateHeadline() {
    const headline = spectacular.querySelector('.spectacular-headline');
    if (!headline) return;

    // Split text into characters for stagger animation
    const text = headline.textContent;
    headline.innerHTML = '';

    text.split('').forEach((char) => {
      const span = document.createElement('span');
      span.textContent = char === ' ' ? '\u00A0' : char;
      span.style.display = 'inline-block';
      headline.appendChild(span);
    });

    const chars = headline.querySelectorAll('span');

    gsap.fromTo(
      chars,
      {
        y: 50,
        opacity: 0,
        rotateX: -90,
      },
      {
        y: 0,
        opacity: 1,
        rotateX: 0,
        duration: 0.8,
        ease: 'back.out(1.7)',
        stagger: 0.03,
        delay: 0.6,
      }
    );
  }

  /**
   * SCROLL INDICATOR PULSE
   * Enhanced pulse animation
   */
  function animateScrollIndicator() {
    const indicator = spectacular.querySelector('.scroll-indicator');
    if (!indicator) return;

    gsap.to(indicator, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: 'power2.out',
      delay: 1.5,
    });

    // Continuous pulse
    gsap.to(indicator.querySelector('.scroll-arrow'), {
      scale: 1.2,
      duration: 0.6,
      ease: 'power1.inOut',
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * FRAME HOVER EFFECT
   * Enhanced interactive hover with GSAP
   */
  function initFrameHover() {
    const frame = spectacular.querySelector('.spectacular-frame');
    if (!frame) return;

    frame.addEventListener('mouseenter', () => {
      gsap.to(frame, {
        rotateZ: 0,
        scale: 1.05,
        duration: 0.4,
        ease: 'elastic.out(1, 0.5)',
      });
    });

    frame.addEventListener('mouseleave', () => {
      gsap.to(frame, {
        rotateZ: -2,
        scale: 1,
        duration: 0.4,
        ease: 'elastic.out(1, 0.5)',
      });
    });
  }

  /**
   * AWARD SEAL HOVER EFFECTS
   * Seals react to mouse proximity
   */
  function initSealHover() {
    const seals = spectacular.querySelectorAll('.award-seal');
    if (!seals.length) return;

    seals.forEach((seal) => {
      seal.addEventListener('mouseenter', () => {
        gsap.to(seal, {
          scale: 1.15,
          rotateZ: `+=${Math.random() * 20 - 10}`,
          duration: 0.3,
          ease: 'back.out(2)',
        });
      });

      seal.addEventListener('mouseleave', () => {
        gsap.to(seal, {
          scale: 1,
          rotateZ: seal.dataset.rotation || 15,
          duration: 0.3,
          ease: 'power2.out',
        });
      });
    });
  }

  /**
   * CURTAIN REVEAL ANIMATION
   * Animate the "FINE, BUY IT ALREADY" section on scroll
   */
  function initCurtainReveal() {
    const curtain = spectacular.querySelector('.details-curtain');
    if (!curtain) return;

    gsap.fromTo(
      curtain,
      {
        y: -100,
        opacity: 0,
        rotateZ: -5,
      },
      {
        y: 0,
        opacity: 1,
        rotateZ: -2,
        duration: 0.8,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: curtain,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );
  }

  /**
   * PRODUCT DETAILS FADE IN
   * Stagger animation for product details
   */
  function initDetailsAnimation() {
    const details = spectacular.querySelector('.product-details-grid');
    if (!details) return;

    const children = details.children;

    gsap.fromTo(
      children,
      {
        y: 50,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power2.out',
        stagger: 0.2,
        scrollTrigger: {
          trigger: details,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      }
    );
  }

  /**
   * ADD TO CART BUTTON EFFECT
   * Enhanced button interaction
   */
  function initButtonEffects() {
    const button = spectacular.querySelector('.btn-add-to-cart');
    if (!button) return;

    button.addEventListener('mouseenter', () => {
      if (!button.disabled) {
        gsap.to(button, {
          scale: 1.05,
          duration: 0.3,
          ease: 'back.out(2)',
        });
      }
    });

    button.addEventListener('mouseleave', () => {
      gsap.to(button, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    });

    button.addEventListener('click', (e) => {
      if (!button.disabled) {
        // Success animation
        gsap.to(button, {
          scale: 0.95,
          duration: 0.1,
          ease: 'power2.in',
          onComplete: () => {
            gsap.to(button, {
              scale: 1,
              duration: 0.3,
              ease: 'elastic.out(1, 0.5)',
            });
          },
        });
      }
    });
  }

  /**
   * OPTION BUTTONS ANIMATION
   * Smooth transitions for variant selectors
   */
  function initOptionButtons() {
    const optionLabels = spectacular.querySelectorAll('.option-values label');
    if (!optionLabels.length) return;

    optionLabels.forEach((label) => {
      label.addEventListener('mouseenter', () => {
        gsap.to(label, {
          y: -3,
          duration: 0.2,
          ease: 'power2.out',
        });
      });

      label.addEventListener('mouseleave', () => {
        gsap.to(label, {
          y: 0,
          duration: 0.2,
          ease: 'power2.out',
        });
      });
    });
  }

  /**
   * VARIANT SWITCHER
   * Handles product variant changes and updates UI
   */
  function initVariantSwitcher() {
    const variantsScript = spectacular.querySelector('script[id^="Spectacular-Variants-"]');
    if (!variantsScript) return;

    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.error('Error parsing variants JSON', e);
      return;
    }

    const form = spectacular.querySelector('[data-spectacular-form]');
    const variantInput = spectacular.querySelector('[data-variant-id]');
    const mainImage = spectacular.querySelector('[data-spectacular-main-image]');
    const addToCartBtn = spectacular.querySelector('.btn-add-to-cart');

    if (!form || !variantInput || !mainImage) return;

    function getSelectedOptions() {
      const options = [];
      const optionInputs = form.querySelectorAll('input[type="radio"]:checked');
      optionInputs.forEach(input => {
        options.push(input.value);
      });
      return options;
    }

    function findVariantByOptions(selectedOptions) {
      return variants.find(variant => {
        return variant.options.every((option, index) => {
          return option === selectedOptions[index];
        });
      });
    }

    function updateImage(newImageUrl) {
      if (!newImageUrl || newImageUrl === mainImage.src) return;
      
      // Use GSAP for smoother transition if available, otherwise fallback
      if (typeof gsap !== 'undefined') {
        gsap.to(mainImage, { opacity: 0, duration: 0.3, onComplete: () => {
          mainImage.src = newImageUrl;
          gsap.to(mainImage, { opacity: 1, duration: 0.3 });
        }});
      } else {
        mainImage.style.opacity = '0';
        setTimeout(() => {
          mainImage.src = newImageUrl;
          mainImage.style.opacity = '1';
        }, 300);
      }
    }

    function updateButton(available) {
      if (available) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = addToCartBtn.dataset.addText || 'ADD TO CART';
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = addToCartBtn.dataset.soldOutText || 'SOLD OUT';
      }
    }

    function handleVariantChange() {
      const selectedOptions = getSelectedOptions();
      const variant = findVariantByOptions(selectedOptions);

      if (variant) {
        variantInput.value = variant.id;
        if (variant.featured_image) {
          updateImage(variant.featured_image);
        }
        updateButton(variant.available);
      }
    }

    const optionInputs = form.querySelectorAll('input[type="radio"]');
    optionInputs.forEach(input => {
      input.addEventListener('change', handleVariantChange);
    });
  }

  /**
   * INITIALIZE ALL ANIMATIONS
   */
  function init() {
    // Wait for page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Initialize entrance animations
    animateProductEntrance();
    animateAwardSeals();
    animateHeadline();
    animateScrollIndicator();

    // Initialize particle effects
    initPaparazziFlashes();
    initConfetti();

    // Initialize hover effects
    initFrameHover();
    initSealHover();
    initButtonEffects();
    initOptionButtons();
    
    // Initialize functional logic
    initVariantSwitcher();

    // Initialize scroll-triggered animations
    if (typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
      initCurtainReveal();
      initDetailsAnimation();
    }
  }

  // Start initialization
  init();

})();
